import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useGetUserProfileQuery } from '../store/api/userApi';
import { Button, Card } from '../styles/components';
import {
  RocketLaunchIcon,
  DocumentTextIcon,
  KeyIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChartBarIcon,
  CodeBracketIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const HelpContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing['2xl']};
`;

const HelpHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing['2xl']};
`;

const HelpIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing.xl};

  svg {
    width: 4rem;
    height: 4rem;
    color: ${props => props.theme.colors.primary[600]};
  }
`;

const HelpTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['3xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const HelpSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.gray[600]};
  max-width: 700px;
  margin: 0 auto;
`;

const Section = styled.section`
  margin-bottom: ${props => props.theme.spacing['2xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  svg {
    width: 1.75rem;
    height: 1.75rem;
    color: ${props => props.theme.colors.primary[600]};
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InfoCard = styled(Card)`
  padding: ${props => props.theme.spacing.xl};
  height: 100%;
`;

const CardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: ${props => props.theme.borders.radius.lg};
  background-color: ${props => props.theme.colors.primary[50]};
  margin-bottom: ${props => props.theme.spacing.md};

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: ${props => props.theme.colors.primary[600]};
  }
`;

const CardTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const CardDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.base};
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.6;
`;

const StepsList = styled.div`
  max-width: 900px;
  margin: 0 auto ${props => props.theme.spacing.xl};
`;

const StepItem = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

const StepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary[600]};
  color: white;
  font-weight: ${props => props.theme.fontWeights.semibold};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h4`
  font-size: ${props => props.theme.fontSizes.base};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const StepDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.sm};
`;

const FeatureIcon = styled.div`
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => props.theme.colors.success[600]};
    margin-top: 0.125rem;
  }
`;

const FeatureText = styled.span`
  font-size: ${props => props.theme.fontSizes.base};
  color: ${props => props.theme.colors.gray[700]};
`;

const CTASection = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing['2xl']};
  background-color: ${props => props.theme.colors.primary[50]};
  border-radius: ${props => props.theme.borders.radius.lg};
  border: 1px solid ${props => props.theme.colors.primary[200]};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const CTATitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const CTADescription = styled.p`
  font-size: ${props => props.theme.fontSizes.base};
  color: ${props => props.theme.colors.gray[600]};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const CreateButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.base};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const CodeBlock = styled.div`
  background-color: ${props => props.theme.colors.gray[900]};
  color: ${props => props.theme.colors.gray[100]};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borders.radius.md};
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: ${props => props.theme.fontSizes.sm};
  overflow-x: auto;
  margin: ${props => props.theme.spacing.md} 0;
  line-height: 1.6;
`;

export function Help() {
  const navigate = useNavigate();
  const { isApiUser, isSupport, isSuperAdmin } = usePermissions();
  const { data: userProfile } = useGetUserProfileQuery();

  const hasClients = userProfile?.hasClients ?? true;

  return (
    <HelpContainer>
      <HelpHeader>
        <HelpIcon>
          <QuestionMarkCircleIcon />
        </HelpIcon>
        <HelpTitle>FinTra Help & Documentation</HelpTitle>
        <HelpSubtitle>
          Everything you need to know about using the Financial Transactions Gateway
        </HelpSubtitle>
      </HelpHeader>

      {/* API User Section */}
      {isApiUser && (
        <>
          <Section>
            <SectionTitle>
              <CodeBracketIcon />
              For API Users
            </SectionTitle>

            <ContentGrid>
              <InfoCard>
                <CardIcon>
                  <KeyIcon />
                </CardIcon>
                <CardTitle>What are FinTra API Clients?</CardTitle>
                <CardDescription>
                  FinTra API Clients are OAuth 2.0 credentials that allow your applications to securely
                  communicate with financial service providers. Each client gets a unique Client ID and
                  Secret for authentication against the Financial Gateway.
                </CardDescription>
              </InfoCard>

              <InfoCard>
                <CardIcon>
                  <ShieldCheckIcon />
                </CardIcon>
                <CardTitle>Security & Authentication</CardTitle>
                <CardDescription>
                  All API requests must be authenticated using OAuth 2.0. Your credentials against the
                  financial institution are stored safely in an Azure Key Vault and used to authenticate
                  each request from your solution to the institution.
                </CardDescription>
              </InfoCard>
            </ContentGrid>

            <StepsList>
              <StepItem>
                <StepNumber>1</StepNumber>
                <StepContent>
                  <StepTitle>Create Your API Client</StepTitle>
                  <StepDescription>
                    Navigate to the Clients page and click "Create Client". You'll need to provide a unique
                    client ID and select the financial provider you want to integrate with. Choose the appropriate
                    authentication method and configure any IP restrictions if needed.
                  </StepDescription>
                </StepContent>
              </StepItem>

              <StepItem>
                <StepNumber>2</StepNumber>
                <StepContent>
                  <StepTitle>Secure Your Credentials</StepTitle>
                  <StepDescription>
                    After creation, you'll receive a Client Secret. Store this securely - it will only
                    be shown once. You can cycle the secret later if needed for security purposes. Never
                    commit credentials to version control or share them publicly.
                  </StepDescription>
                </StepContent>
              </StepItem>

              <StepItem>
                <StepNumber>3</StepNumber>
                <StepContent>
                  <StepTitle>Authenticate & Integrate</StepTitle>
                  <StepDescription>
                    You can integrate with FinTra in two ways:
                  </StepDescription>
                  <StepDescription style={{ marginTop: '0.75rem', fontWeight: 500 }}>
                    Option 1: Business System Integration
                  </StepDescription>
                  <StepDescription>
                    Simply plug your FinTra Client ID and Secret into your business system
                    (Business Central, SAP, or other ERP systems) where all authentication and
                    API communication is handled automatically by the system.
                  </StepDescription>
                  <StepDescription style={{ marginTop: '0.75rem', fontWeight: 500 }}>
                    Option 2: Direct API Integration
                  </StepDescription>
                  <StepDescription>
                    Use your Client ID and Secret to obtain an access token via OAuth 2.0 Client
                    Credentials flow, then include this token in the Authorization header of your API requests.
                  </StepDescription>
                  <CodeBlock>
{`POST /connect/token
Content-Type: application/x-www-form-urlencoded

grant_type=client_credentials
&client_id=YOUR_CLIENT_ID
&client_secret=YOUR_CLIENT_SECRET
&scope=api`}
                  </CodeBlock>
                </StepContent>
              </StepItem>

              <StepItem>
                <StepNumber>4</StepNumber>
                <StepContent>
                  <StepTitle>Monitor & Manage</StepTitle>
                  <StepDescription>
                    Use the Logs page to monitor your API usage, track errors, and ensure your
                    integrations are running smoothly. You can view detailed information about each request
                    including response times, status codes, and error messages.
                  </StepDescription>
                </StepContent>
              </StepItem>
            </StepsList>

            {!hasClients && (
              <CTASection>
                <CTATitle>Ready to Get Started?</CTATitle>
                <CTADescription>
                  Create your first API client to begin integrating with the Financial Transactions Gateway
                </CTADescription>
                <CreateButton onClick={() => navigate('/clients')}>
                  Create Your First Client
                  <ArrowRightIcon />
                </CreateButton>
              </CTASection>
            )}

            <SectionTitle>Available Features</SectionTitle>
            <FeaturesGrid>
              <FeatureItem>
                <FeatureIcon><CheckCircleIcon /></FeatureIcon>
                <FeatureText>View and manage API clients</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon><CheckCircleIcon /></FeatureIcon>
                <FeatureText>Monitor API request logs</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon><CheckCircleIcon /></FeatureIcon>
                <FeatureText>Track performance metrics</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon><CheckCircleIcon /></FeatureIcon>
                <FeatureText>Manage client credentials</FeatureText>
              </FeatureItem>              
            </FeaturesGrid>
          </Section>
        </>
      )}

      {/* Support User Section */}
      {isSupport && (
        <Section>
          <SectionTitle>
            <ShieldCheckIcon />
            For Support Users
          </SectionTitle>

          <ContentGrid>
            <InfoCard>
              <CardIcon>
                <BuildingOfficeIcon />
              </CardIcon>
              <CardTitle>Organization Management</CardTitle>
              <CardDescription>
                Manage organizations and their associated API clients. Create, update, and monitor
                organizations across different business systems. Track API client counts and
                management client statistics for each organization.
              </CardDescription>
            </InfoCard>

            <InfoCard>
              <CardIcon>
                <UsersIcon />
              </CardIcon>
              <CardTitle>Client Administration</CardTitle>
              <CardDescription>
                View and manage all API clients across organizations. Monitor client status, configure
                security settings, manage credentials, and troubleshoot integration issues. Access
                detailed client information including scopes, IP restrictions, and activity logs.
              </CardDescription>
            </InfoCard>

            <InfoCard>
              <CardIcon>
                <KeyIcon />
              </CardIcon>
              <CardTitle>Management Clients</CardTitle>
              <CardDescription>
                Create and manage OAuth 2.0 management clients for administrative access. These clients
                can have admin or support scopes for managing organizations and API clients programmatically.
                Cycle secrets and configure permissions as needed.
              </CardDescription>
            </InfoCard>

            <InfoCard>
              <CardIcon>
                <ChartBarIcon />
              </CardIcon>
              <CardTitle>Monitoring & Logs</CardTitle>
              <CardDescription>
                Access comprehensive logging and monitoring tools. Track API requests, monitor performance
                metrics, identify errors, and analyze usage patterns. Filter logs by client, organization,
                endpoint, and time period for detailed insights.
              </CardDescription>
            </InfoCard>
          </ContentGrid>

          {!isSuperAdmin && (
            <InfoCard>
              <CardIcon>
                <LockClosedIcon />
              </CardIcon>
              <CardTitle>Business System Scope</CardTitle>
              <CardDescription>
                Your account is scoped to a specific business system. You can only view and manage
                organizations, clients, and data within your assigned business system. This ensures
                proper data isolation and security across different business units.
              </CardDescription>
            </InfoCard>
          )}

          {isSuperAdmin && (
            <InfoCard>
              <CardIcon>
                <DocumentTextIcon />
              </CardIcon>
              <CardTitle>Certificate Management</CardTitle>
              <CardDescription>
                Monitor SSL/TLS certificates used across the gateway. Track expiration dates, view
                certificate details, and receive alerts for certificates nearing expiration. Export
                certificate reports for compliance and audit purposes.
              </CardDescription>
            </InfoCard>
          )}
        </Section>
      )}

      {/* Common Documentation Section */}
      <Section>
        <SectionTitle>
          <DocumentTextIcon />
          Additional Resources
        </SectionTitle>

        <FeaturesGrid>
          <InfoCard>
            <CardTitle>OAuth 2.0 Flow</CardTitle>
            <CardDescription>
              FinTra uses the OAuth 2.0 Client Credentials grant type for machine-to-machine
              authentication. Tokens are short-lived and should be refreshed before expiration.
            </CardDescription>
          </InfoCard>

          <InfoCard>
            <CardTitle>Best Practices</CardTitle>
            <CardDescription>
              Always use HTTPS, rotate secrets regularly, implement proper error handling,
              cache access tokens appropriately, and monitor your API usage for anomalies.
            </CardDescription>
          </InfoCard>

          <InfoCard>
            <CardTitle>Error Handling</CardTitle>
            <CardDescription>
              API responses include standard HTTP status codes. 401 indicates authentication
              failure, 403 for authorization issues, and 429 for rate limiting. Check the Logs
              page for detailed error information.
            </CardDescription>
          </InfoCard>
        </FeaturesGrid>
      </Section>
    </HelpContainer>
  );
}
